import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Plus, Pencil, Trash2, X } from 'lucide-react';

const PRESET_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const EMPTY_FORM = { name: '', type: 'expense', color: PRESET_COLORS[0], monthly_budget: '' };

export function Categories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    const fetchCategories = () => {
        setLoading(true);
        api.getCategories()
            .then(setCategories)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setFormErrors({});
        setShowForm(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormErrors({});

        const errors = {};
        if (!form.name.trim()) errors.name = 'Inserisci un nome.';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const payload = {
            ...form,
            name: form.name.trim(),
            monthly_budget: form.monthly_budget ? parseFloat(form.monthly_budget) : null,
        };

        const promise = editingId
            ? api.updateCategory(editingId, payload)
            : api.createCategory(payload);

        promise
            .then(() => {
                resetForm();
                fetchCategories();
            })
            .catch((err) => {
                const backendErrors = err.response?.data?.errors;
                if (backendErrors) {
                    setFormErrors(Object.fromEntries(
                        Object.entries(backendErrors).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : v])
                    ));
                } else {
                    setFormErrors({ generic: err.response?.data?.message || 'Errore durante il salvataggio.' });
                }
            });
    };

    const handleEdit = (category) => {
        setEditingId(category.id);
        setForm({
            name: category.name,
            type: category.type,
            color: category.color || PRESET_COLORS[0],
            monthly_budget: category.monthly_budget || '',
        });
        setFormErrors({});
        setShowForm(true);
    };

    const handleDelete = (id) => {
        if (confirm('Eliminare questa categoria?')) {
            api.deleteCategory(id).then(fetchCategories);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Categorie</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowForm(!showForm);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition"
                >
                    <Plus size={16} />
                    Nuova categoria
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">
                            {editingId ? 'Modifica categoria' : 'Nuova categoria'}
                        </h3>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="p-1 text-gray-400 hover:text-gray-600"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    {formErrors.generic && (
                        <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 rounded-lg">{formErrors.generic}</div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                                required
                            />
                            {formErrors.name && <p className="text-xs text-red-600 mt-1">{formErrors.name}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                            <select
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            >
                                <option value="expense">Spesa</option>
                                <option value="income">Entrata</option>
                            </select>
                            {formErrors.type && <p className="text-xs text-red-600 mt-1">{formErrors.type}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Budget mensile</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.monthly_budget}
                                onChange={(e) => setForm({ ...form, monthly_budget: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            />
                            {formErrors.monthly_budget && <p className="text-xs text-red-600 mt-1">{formErrors.monthly_budget}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Colore</label>
                            <div className="flex gap-2 flex-wrap">
                                {PRESET_COLORS.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => setForm({ ...form, color: c })}
                                        className={`w-7 h-7 rounded-full border-2 ${form.color === c ? 'border-gray-900' : 'border-transparent'}`}
                                        style={{ backgroundColor: c }}
                                    />
                                ))}
                            </div>
                            {formErrors.color && <p className="text-xs text-red-600 mt-1">{formErrors.color}</p>}
                        </div>
                        <div className="flex items-end">
                            <button type="submit" className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition">
                                {editingId ? 'Aggiorna' : 'Salva'}
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {loading ? (
                <p className="text-gray-500">Caricamento...</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {categories.map((category) => (
                        <div key={category.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-4 h-12 rounded-full" style={{ backgroundColor: category.color || '#cbd5e1' }} />
                                <div>
                                    <h3 className="font-semibold text-gray-900">{category.name}</h3>
                                    <p className="text-xs text-gray-500 capitalize">{category.type === 'income' ? 'Entrata' : 'Spesa'}</p>
                                    {category.monthly_budget && (
                                        <p className="text-xs text-gray-500 mt-1">Budget: € {parseFloat(category.monthly_budget).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => handleEdit(category)}
                                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                    title="Modifica"
                                >
                                    <Pencil size={16} />
                                </button>
                                <button
                                    onClick={() => handleDelete(category.id)}
                                    className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                    title="Elimina"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
